import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/context/ProductContext";
import { supabase } from "@/integrations/supabase/client";
import { createPublicOrder } from "@/lib/store.functions";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Trash2, Plus, Minus, MessageCircle, ShoppingBag, User, Phone, Loader2, CheckCircle2, AlertCircle, ShoppingCart } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function CartView() {
  const { items, totalPrice, updateQuantity, removeFromCart, customerName, customerPhone, setCustomerInfo, clearCart } = useCart();
  const { getSetting } = useProducts();
  const [name, setName] = useState(customerName);
  const [phone, setPhone] = useState(customerPhone);
  const [isFetchingName, setIsFetchingName] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    setName(customerName);
    setPhone(customerPhone);
  }, [customerName, customerPhone]);

  const validatePhoneAndFetchName = async (phoneNumber: string) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) return;
    
    setIsValidating(true);
    setPhoneError(null);
    setIsFetchingName(true);
    
    try {
      const uazInstance = await getSetting('uazapi_instance');
      if (!uazInstance) {
        setIsFetchingName(false);
        setIsValidating(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('uazapi-proxy', {
        body: { 
          action: 'get_contact_name', 
          instanceName: uazInstance,
          phone: cleanPhone 
        }
      });

      if (error) throw error;

      if (data?.exists === false) {
        setPhoneError("Este número não possui WhatsApp");
        toast.error("Número não encontrado no WhatsApp");
      } else if (data?.exists === true) {
        setPhoneError(null);
        if (data.name) {
          setName(data.name);
          toast.success(`Nome encontrado: ${data.name}`);
        }
      }
    } catch (err) {
      console.error("Erro ao validar telefone:", err);
    } finally {
      setIsFetchingName(false);
      setIsValidating(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPhone(val);
    const clean = val.replace(/\D/g, '');
    if (clean.length >= 10) {
      const timeoutId = setTimeout(() => validatePhoneAndFetchName(clean), 1000);
      return () => clearTimeout(timeoutId);
    } else {
      setPhoneError(null);
    }
  };

  const handleCheckout = async () => {
    if (!name || !phone || !!phoneError || isValidating || isSubmitting) return;

    setIsSubmitting(true);
    setCustomerInfo(name, phone);
    
    try {
      const result = await createPublicOrder({
        customerName: name,
        customerPhone: phone,
        items: items.map(i => ({ id: i.id, quantity: i.quantity }))
      });

      if (!result.id) throw new Error("Erro ao criar pedido");

      toast.success("Encomenda enviada com sucesso!");
      setShowSuccessDialog(true);
      clearCart();

      toast.success("Encomenda enviada com sucesso!");
      setShowSuccessDialog(true);
      clearCart();
    } catch (err) {
      console.error("Erro ao finalizar encomenda:", err);
      toast.error(err instanceof Error ? err.message : "Erro ao processar encomenda. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0 && !showSuccessDialog) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] px-6 text-center">
        <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mb-4">
          <ShoppingBag className="h-10 w-10 text-pink-200" />
        </div>
        <h3 className="text-lg font-medium text-gray-900">Sua encomenda está vazia</h3>
        <p className="text-sm text-gray-500 mt-2">Escolha seus produtos favoritos e adicione-os aqui.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white relative">
      <ScrollArea className="flex-1 px-6">
        <div className="space-y-6 py-4">
          {items.map((item) => (
            <div key={item.id} className="flex gap-4">
              <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100">
                <img src={item.images[0]} alt={item.name} className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-1 flex-col justify-between py-0.5">
                <div>
                  <div className="flex justify-between items-start">
                    <h4 className="text-xs font-semibold text-gray-900 leading-tight pr-4 line-clamp-2">{item.name}</h4>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-pink-600">
                    {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(item.price)}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <button 
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="h-6 w-6 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="text-xs font-medium w-4 text-center">{item.quantity}</span>
                  <button 
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="h-6 w-6 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="bg-white border-t p-6 pb-10 space-y-6 shadow-[0_-8px_30px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Subtotal</span>
          <span className="text-xl font-bold text-gray-900">
            {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(totalPrice)}
          </span>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">WhatsApp</label>
            <div className="relative group">
              <Phone className={`absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors ${phoneError ? 'text-red-500' : 'text-gray-400 group-focus-within:text-pink-500'}`} />
              <input
                type="tel"
                placeholder="Ex: 11999999999"
                value={phone}
                onChange={handlePhoneChange}
                className={`h-12 w-full rounded-2xl border-2 ${phoneError ? 'border-red-100 bg-red-50/50 focus:border-red-200' : 'border-gray-50 bg-gray-50/50 focus:border-pink-100 focus:bg-white'} pl-11 pr-10 text-sm font-medium transition-all focus:outline-none focus:ring-4 focus:ring-pink-50/50`}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {isValidating ? (
                  <Loader2 className="h-4 w-4 text-pink-500 animate-spin" />
                ) : phone.replace(/\D/g, '').length >= 10 && !phoneError ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : phoneError ? (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                ) : null}
              </div>
            </div>
            {phoneError && (
              <p className="text-[10px] text-red-500 font-bold px-2 flex items-center gap-1.5">
                <AlertCircle className="h-3 w-3" />
                {phoneError}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Seu Nome</label>
            <div className="relative group">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 transition-colors group-focus-within:text-pink-500" />
              <input
                type="text"
                placeholder="Como quer ser chamado?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 w-full rounded-2xl border-2 border-gray-50 bg-gray-50/50 pl-11 pr-10 text-sm font-medium transition-all focus:outline-none focus:border-pink-100 focus:bg-white focus:ring-4 focus:ring-pink-50/50"
              />
              {isFetchingName && (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-pink-500 animate-spin" />
              )}
            </div>
          </div>
        </div>

        <Button 
          onClick={handleCheckout}
          disabled={!name || !phone || !!phoneError || isValidating || isSubmitting}
          className="w-full h-14 rounded-2xl bg-pink-500 hover:bg-pink-600 text-white text-base font-extrabold shadow-xl shadow-pink-200/50 transition-all active:scale-[0.97] disabled:opacity-50 disabled:shadow-none"
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            <MessageCircle className="mr-2 h-5 w-5" />
          )}
          Confirmar Encomenda
        </Button>
      </div>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-[90vw] w-full sm:max-w-[400px] rounded-[32px] p-8 border-none flex flex-col items-center text-center shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-300">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
          </div>
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl font-bold text-gray-900">Encomenda Enviada!</DialogTitle>
            <p className="text-sm text-gray-500 font-medium">
              Olá <span className="text-pink-600 font-bold">{name}</span>, sua encomenda já está com a Gaby.
            </p>
          </DialogHeader>
          
          <div className="bg-gray-50 rounded-2xl p-4 mt-6 w-full">
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">Próximo passo</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Você receberá uma confirmação automática no seu WhatsApp em instantes.
            </p>
          </div>

          <Button 
            onClick={() => setShowSuccessDialog(false)}
            className="mt-8 w-full h-14 rounded-2xl bg-gray-900 hover:bg-black text-white font-bold transition-all active:scale-[0.97]"
          >
            Excelente!
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}